/* eslint-disable react/require-default-props -- defaults via destructuring; no defaultProps (React 19) */
import React from 'react';
import PropTypes from 'prop-types';

const THUMBS_UP_VIEWBOX = 14;

const THUMBS_UP_PATH =
  'M5.24622 10.5624L5.82914 8.16083H2.43074C2.06378 8.16083 1.71824 7.98806 1.49807 7.6945C1.2779 7.40094 1.20879 7.02084 1.31154 6.66857L2.66973 2.00523C2.81481 1.50781 3.27078 1.16583 3.78893 1.16583H11.6583C12.3022 1.16583 12.8241 1.68779 12.8241 2.33167V6.995C12.8241 7.63887 12.3022 8.16083 11.6583 8.16083H10.0495C9.60706 8.16107 9.2029 8.41169 9.00604 8.80787L6.99497 12.8242C6.43808 12.8173 5.91484 12.5564 5.57417 12.1158C5.2335 11.6752 5.11273 11.1031 5.24622 10.5624M9.90956 8.16083V1.16583';

const THUMBS_UP_DEFAULT_STROKE_WIDTH = 1.16583;

/** Figma default stroke (`#737373` → nearest `--pal-grey-black-500`). */
export const THUMBS_UP_ICON_DEFAULT_STROKE = 'var(--pal-grey-black-500)';

const iconPropTypes = {
  variant: PropTypes.oneOf(['outline', 'filled']),
  filled: PropTypes.bool,
  size: PropTypes.number,
  color: PropTypes.string,
  stroke: PropTypes.string,
  strokeWidth: PropTypes.number,
  fill: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.shape({}),
  'aria-label': PropTypes.string,
  'aria-hidden': PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

/**
 * Thumbs up (14×14). Outline by default; pass `filled` or `variant="filled"` for solid.
 * Color via `color`, `stroke`, `fill`, or CSS `color` + `currentColor`.
 */
const ThumbsUpIcon = ({
  variant = 'outline',
  filled,
  size = 14,
  color,
  stroke,
  strokeWidth,
  fill,
  className = '',
  style,
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden = true,
  ...props
}) => {
  const isFilled = filled ?? variant === 'filled';
  const resolvedSize = size;
  const baseStrokeWidth = strokeWidth ?? THUMBS_UP_DEFAULT_STROKE_WIDTH;
  const scaledStrokeWidth = (baseStrokeWidth * resolvedSize) / THUMBS_UP_VIEWBOX;
  const pathFill = fill ?? (isFilled ? color || 'currentColor' : 'none');
  const pathStroke = color || stroke || 'currentColor';

  return (
    <svg
      width={resolvedSize}
      height={resolvedSize}
      viewBox={`0 0 ${THUMBS_UP_VIEWBOX} ${THUMBS_UP_VIEWBOX}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={['ds-icon', isFilled && 'ds-icon--filled', className].filter(Boolean).join(' ')}
      style={color ? { color, ...style } : style}
      aria-hidden={ariaLabel ? undefined : ariaHidden}
      aria-label={ariaLabel || undefined}
      role={ariaLabel ? 'img' : undefined}
      focusable="false"
      {...props}
    >
      <path
        d={THUMBS_UP_PATH}
        fill={pathFill}
        stroke={pathStroke}
        strokeWidth={scaledStrokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

ThumbsUpIcon.propTypes = iconPropTypes;

const ThumbsUpFilledIcon = (props) => <ThumbsUpIcon variant="filled" {...props} />;

ThumbsUpFilledIcon.propTypes = iconPropTypes;

const ThumbsUpOutlineIcon = (props) => <ThumbsUpIcon variant="outline" {...props} />;

ThumbsUpOutlineIcon.propTypes = iconPropTypes;

export { ThumbsUpIcon, ThumbsUpFilledIcon, ThumbsUpOutlineIcon };
export default ThumbsUpIcon;
